/**
 * Interface translation: one catalogue per language, one hook to read it.
 *
 * No library. What this application needs — a key, an occasional parameter, a
 * language switch — is the part of `i18next` that fits in these four files; the
 * rest of it, namespaces, lazy loading and backends, would be configuration to
 * maintain for behaviour nobody here would use (D260814d).
 *
 * This file holds only what needs React. Reading a catalogue lives in
 * `translate.ts`, which the tests use directly.
 */

import type { Locale } from '@kanto/shared';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { activeLocale, setActiveLocale, writeStoredLocale } from './locale';
import { makeTranslate, type Translate } from './translate';

export { AVAILABLE_LOCALES, LOCALE_NAMES, makeTranslate } from './translate';
export type { MessageKey, Messages, Translate } from './translate';

interface I18n {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translate;
}

const I18nContext = createContext<I18n | null>(null);

/**
 * Holds the language in force for the whole application.
 *
 * Mounted above the router in `main.tsx`: a page reading a message before the
 * language is known would render one sentence in English and re-render it in
 * French, which is exactly the flicker this avoids.
 */
export function LocaleProvider({ children }: { children: ReactNode }): ReactElement {
  const [locale, setLocaleState] = useState<Locale>(() => activeLocale());

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    // Remember before rendering: switching language is a decision, and finding
    // the interface back in the other language on the next visit reads as a bug.
    writeStoredLocale(next);
    // Keeps `api/client.ts` and the `lang` attribute in step: the next request
    // has to announce the language that has just been chosen, so the server's
    // refusals arrive in it.
    setActiveLocale(next);
  }, []);

  const value = useMemo<I18n>(
    () => ({ locale, setLocale, t: makeTranslate(locale) }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

function useI18n(): I18n {
  const context = useContext(I18nContext);
  if (!context) throw new Error('LocaleProvider is missing above this component');
  return context;
}

/** The translation function. `const t = useT();` then `t('topbar.signOut')`. */
export function useT(): Translate {
  return useI18n().t;
}

/**
 * The language in force, and how to change it.
 *
 * Separate from `useT` because only the switcher changes the language, while
 * every component reads messages.
 */
export function useLocale(): { locale: Locale; setLocale: (locale: Locale) => void } {
  const { locale, setLocale } = useI18n();
  return { locale, setLocale };
}
